import { EnrichedEntity } from '@/lib/types';
import { RefObject, useState } from 'react';

interface FloatingCard {
  entity: EnrichedEntity;
  position: { x: number; y: number };
}

export function useFloatingCards(editorAreaRef: RefObject<HTMLDivElement>) {
  const [floatingCards, setFloatingCards] = useState<Map<string, FloatingCard>>(new Map());
  const [draggedEntity, setDraggedEntity] = useState<EnrichedEntity | null>(null);

  const handleEntityDragStart = (entity: EnrichedEntity, event: React.DragEvent) => {
    setDraggedEntity(entity);
    event.dataTransfer.effectAllowed = 'copy';
    // Required for some browsers to allow drop, but empty to avoid pasting UID
    event.dataTransfer.setData('text/plain', '');
    event.dataTransfer.setData('application/json', JSON.stringify(entity));
  };

  const handleDrop = (event: React.DragEvent) => {
    if (!draggedEntity || !editorAreaRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.nativeEvent) {
      event.nativeEvent.stopImmediatePropagation();
      event.nativeEvent.stopPropagation();
    }

    const rect = editorAreaRef.current.getBoundingClientRect();
    const dropX = event.clientX - rect.left;
    const dropY = event.clientY - rect.top;

    const cardWidth = 280;
    const cardHeight = 350; // Estimated height
    const margin = 16;
    
    // Constrain x and y to keep card within bounds
    const x = Math.max(margin, Math.min(dropX - (cardWidth / 2), rect.width - cardWidth - margin));
    const y = Math.max(margin, Math.min(dropY - 20, rect.height - cardHeight - margin));

    setFloatingCards(prev => {
      const newCards = new Map(prev);
      newCards.set(draggedEntity.id || '', {
        entity: draggedEntity,
        position: { x, y },
      });
      return newCards;
    });

    setDraggedEntity(null);
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (!draggedEntity) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.nativeEvent) {
      event.nativeEvent.stopImmediatePropagation();
      event.nativeEvent.stopPropagation();
    }
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleCloseCard = (entityId: string) => {
    setFloatingCards(prev => {
      const newCards = new Map(prev);
      newCards.delete(entityId);
      return newCards;
    });
  };

  const handleCardPositionChange = (entityId: string, position: { x: number; y: number }) => {
    setFloatingCards(prev => {
      const newCards = new Map(prev);
      const card = newCards.get(entityId);
      if (card) {
        newCards.set(entityId, { ...card, position });
      }
      return newCards;
    });
  };

  return {
    floatingCards,
    setFloatingCards,
    handleEntityDragStart,
    handleDrop,
    handleDragOver,
    handleCloseCard,
    handleCardPositionChange,
  };
}
